/**
 * V2 Conformance — repair normalizer
 *
 * Rewrites legacy paths/shapes to their canonical V2 form and strips the stale
 * legacy copies so theme propagation (which writes canonical `.appearance.*`
 * paths) reaches the data again. Driven by the SAME rule map as the validator.
 *
 * Contract (mirrors `ScreenStructNormalizer.normalizeScreenStruct`):
 *   - PURE: deep-clones its input, never mutates it, no Firebase/network imports.
 *   - migrate-then-strip, never lossy (AC-009/AC-012).
 *   - idempotent: normalize(normalize(x)) === normalize(x) (AC-011).
 *
 * @module screen-utils/v2-conformance
 */

import { runConformance, type ConformanceChange } from './ruleEngine';
import { isPlainObject } from './pathPattern';

export interface NormalizeResult<T = unknown> {
  /** The repaired content (a new object; input is never mutated). */
  content: T;
  /** Every legacy occurrence that was migrated/stripped (empty = already clean). */
  changes: ConformanceChange[];
}

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/**
 * Normalize a single StepContent. Returns a repaired clone and the change log.
 * Already-canonical input returns `changes: []` and a structurally-equal clone.
 *
 * @param onlyRuleIds optional rule-id allow-list — a targeted repair (e.g.
 *   `['label.insets']`) applies ONLY those rules, leaving lossy/unrelated drift
 *   (e.g. the `copy.subtitle2` drop) untouched. Omit = the full rule set.
 */
export function normalizeStepContentV2<T = unknown>(content: T, onlyRuleIds?: string[]): NormalizeResult<T> {
  if (!isPlainObject(content)) return { content, changes: [] };
  const clone = cloneJson(content);
  const changes = runConformance(clone, true, onlyRuleIds);
  return { content: clone as unknown as T, changes };
}

/** Normalize every screen in a FlowDocument-shaped object. Pure. `onlyRuleIds` scopes the rules. */
export function normalizeFlowDocumentV2<T = unknown>(flowDocument: T, onlyRuleIds?: string[]): NormalizeResult<T> {
  if (!isPlainObject(flowDocument)) return { content: flowDocument, changes: [] };
  const clone = cloneJson(flowDocument) as Record<string, unknown>;
  const changes: ConformanceChange[] = [];
  const screens = clone.screens;
  if (isPlainObject(screens)) {
    for (const node of Object.values(screens)) {
      if (isPlainObject(node) && isPlainObject(node.content)) {
        changes.push(...runConformance(node.content, true, onlyRuleIds));
      }
    }
  }
  return { content: clone as unknown as T, changes };
}

/**
 * Normalize a ThemeV2 — both its template `flowDocument` screens AND its
 * top-level `headerBar`/`actionBar`/`background` fallback fields. Pure.
 * `onlyRuleIds` scopes the rules (see normalizeStepContentV2).
 */
export function normalizeThemeV2<T = unknown>(theme: T, onlyRuleIds?: string[]): NormalizeResult<T> {
  if (!isPlainObject(theme)) return { content: theme, changes: [] };
  const clone = cloneJson(theme) as Record<string, unknown>;
  const changes: ConformanceChange[] = [];

  if (isPlainObject(clone.flowDocument)) {
    const res = normalizeFlowDocumentV2(clone.flowDocument, onlyRuleIds);
    clone.flowDocument = res.content;
    changes.push(...res.changes);
  }

  // Theme-level fallbacks: run the StepContent rules over a synthetic wrapper,
  // then write the repaired values back onto the theme's own keys.
  const synthetic: Record<string, unknown> = {};
  if (isPlainObject(clone.headerBar)) synthetic.header = clone.headerBar;
  if (isPlainObject(clone.actionBar)) synthetic.actionBar = clone.actionBar;
  if (clone.background !== undefined) synthetic.canvas = { background: clone.background };
  if (Object.keys(synthetic).length) {
    changes.push(...runConformance(synthetic, true, onlyRuleIds));
    if ('header' in synthetic) clone.headerBar = synthetic.header;
    if ('actionBar' in synthetic) clone.actionBar = synthetic.actionBar;
    if (isPlainObject(synthetic.canvas)) clone.background = (synthetic.canvas as Record<string, unknown>).background;
  }

  return { content: clone as unknown as T, changes };
}
